import * as Cesium from "cesium";
import { getUsernameAndUserIdFormat } from "./Utility";

export function rearrangePoints(entitiesOrEntity, stepMeters, setMapObjects, options, userData) {
  function distance3D(a, b) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const dz = b.z - a.z;
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }

  function lerp3D(a, b, t) {
    return {
      x: a.x + (b.x - a.x) * t,
      y: a.y + (b.y - a.y) * t,
      z: a.z + (b.z - a.z) * t,
    };
  }

  function isCollinearMetric(prev, mid, next, maxDeviation, minSegLength) {
    const vx = next.x - prev.x;
    const vy = next.y - prev.y;
    const vz = next.z - prev.z;

    const wx = mid.x - prev.x;
    const wy = mid.y - prev.y;
    const wz = mid.z - prev.z;

    const segLenSq = vx * vx + vy * vy + vz * vz;
    const segLen = Math.sqrt(segLenSq);

    if (segLen < minSegLength) return false;

    const t = (vx * wx + vy * wy + vz * wz) / segLenSq;
    if (t <= 0 || t >= 1) return false;

    const projX = prev.x + vx * t;
    const projY = prev.y + vy * t;
    const projZ = prev.z + vz * t;

    const dx = mid.x - projX;
    const dy = mid.y - projY;
    const dz = mid.z - projZ;

    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
    return dist <= maxDeviation;
  }

  function removeCollinearPoints(points, maxDeviation, minSegLength) {
    if (!points || points.length < 3) {
      return points ? points.slice() : [];
    }

    const cleaned = [];
    cleaned.push(points[0]);

    for (let i = 1; i < points.length - 1; i++) {
      const prev = points[i - 1];
      const mid = points[i];
      const next = points[i + 1];

      if (!isCollinearMetric(prev, mid, next, maxDeviation, minSegLength)) {
        cleaned.push(mid);
      }
    }

    cleaned.push(points[points.length - 1]);
    return cleaned;
  }

  function resamplePolyline(points, stepMeters) {
    if (!points || points.length < 2) {
      return points ? points.slice() : [];
    }

    const resampled = [];
    const step = stepMeters;
    const tEps = 1e-9;

    resampled.push(points[0]);

    for (let i = 0; i < points.length - 1; i++) {
      const start = points[i];
      const end = points[i + 1];

      const segLength = distance3D(start, end);
      if (segLength <= step) {
        resampled.push(end);
        continue;
      }

      const maxK = Math.floor(segLength / step);

      for (let k = 1; k <= maxK; k++) {
        const distAlong = k * step;
        const t = distAlong / segLength;

        if (t >= 1 - tEps) break;

        resampled.push(lerp3D(start, end, t));
      }

      resampled.push(end);
    }

    return resampled;
  }

  function processPolyline(points, stepMeters, maxDeviation, minSegLength) {
    const cleaned = removeCollinearPoints(points, maxDeviation, minSegLength);
    const resampled = resamplePolyline(cleaned, stepMeters);
    return resampled;
  }

  function getLayerIdFromEntity(entity) {
    const collection = entity.entityCollection || entity._entityCollection || (entity._source && entity._source._entityCollection) || null;

    const dataSource = collection && collection.owner ? collection.owner : null;
    if (!dataSource) return null;

    if (dataSource.layerId != null) return dataSource.layerId;
    if (dataSource.id != null) return dataSource.id;
    if (dataSource.name != null) return dataSource.name;

    return null;
  }

  if (!entitiesOrEntity) {
    return { undo: [] };
  }

  const entities = Array.isArray(entitiesOrEntity) ? entitiesOrEntity : [entitiesOrEntity];

  const maxDeviationMeters = options && options.maxDeviationMeters != null ? options.maxDeviationMeters : 1e-3;

  const minSegLengthMeters = options && options.minSegLengthMeters != null ? options.minSegLengthMeters : 1e-6;

  const now = typeof Cesium !== "undefined" && Cesium.JulianDate ? Cesium.JulianDate.now() : undefined;
  const modifier = getUsernameAndUserIdFormat(userData);

  const canUpdateMapObjects = typeof setMapObjects === "function";

  const undoItems = [];

  entities.forEach(function (entity) {
    if (!entity.polyline || !entity.polyline.positions) return;

    let positions;
    if (typeof entity.polyline.positions.getValue === "function" && now) {
      positions = entity.polyline.positions.getValue(now);
    } else {
      positions = entity.polyline.positions;
    }

    if (!positions || positions.length < 2) return;

    const oldPositions = positions.map(function (p) {
      return Cesium.Cartesian3.clone(p);
    });

    const pts = oldPositions.map(function (p) {
      return { x: p.x, y: p.y, z: p.z };
    });

    const newPts = processPolyline(pts, stepMeters, maxDeviationMeters, minSegLengthMeters);

    const newPositions = newPts.map(function (p) {
      return new Cesium.Cartesian3(p.x, p.y, p.z);
    });

    entity.polyline.positions = new Cesium.ConstantProperty(newPositions);

    if (modifier) {
      const currentProps = entity.properties?.getValue ? entity.properties.getValue(now) || {} : entity.properties || {};
      entity.properties = new Cesium.PropertyBag({
        ...currentProps,
        modifier,
      });
    }

    undoItems.push({
      entity,
      oldPositions,
      newPositions,
    });

    if (canUpdateMapObjects) {
      const layerId = getLayerIdFromEntity(entity);

      if (layerId != null) {
        setMapObjects((prev) => prev.map((obj) => (obj && obj.id === layerId ? { ...obj, is_edited: true } : obj)));
      }
    }
  });

  return { undo: undoItems };
}

export function blinkEntity(viewer, entity, options) {
  options = options || {};
  const zoom = options.zoom === true;
  const duration = options.duration || 2000; // ms
  const step = options.step || 200; // ms

  const polyline = entity.polyline;
  if (!polyline) return;

  if (entity._blinkHandler) {
    viewer.clock.onTick.removeEventListener(entity._blinkHandler);
    entity._blinkHandler = null;
  }

  const originalMaterial = polyline.material;
  const originalWidthProp = polyline.width;

  let originalWidthValue;
  if (originalWidthProp && originalWidthProp.getValue) {
    originalWidthValue = originalWidthProp.getValue(viewer.clock.currentTime);
  } else {
    originalWidthValue = originalWidthProp;
  }
  if (typeof originalWidthValue !== "number") {
    originalWidthValue = 2;
  }

  const blinkColor = Cesium.Color.fromCssColorString("yellow");
  const blinkMaterial = new Cesium.ColorMaterialProperty(blinkColor);

  let isBlinkOn = false;

  const startTime = viewer.clock.currentTime.clone();
  let lastToggle = startTime.clone();

  function handler(clock) {
    const now = clock.currentTime;

    const elapsedMs = Cesium.JulianDate.secondsDifference(now, startTime) * 1000.0;
    if (elapsedMs >= duration) {
      polyline.material = originalMaterial;
      polyline.width = originalWidthProp;
      viewer.clock.onTick.removeEventListener(handler);
      entity._blinkHandler = null;
      return;
    }

    const sinceToggleMs = Cesium.JulianDate.secondsDifference(now, lastToggle) * 1000.0;
    if (sinceToggleMs >= step) {
      isBlinkOn = !isBlinkOn;
      polyline.material = isBlinkOn ? blinkMaterial : originalMaterial;
      polyline.width = isBlinkOn ? originalWidthValue * 1.6 : originalWidthProp;

      lastToggle = now.clone();
    }
  }

  viewer.clock.onTick.addEventListener(handler);
  entity._blinkHandler = handler;

  if (zoom) {
    viewer.flyTo(entity, { duration: 1.0 });
  }
}

export function getPropertyKeysFromDataSource(dataSource) {
  if (!dataSource || !dataSource.entities) return [];

  const entities = dataSource.entities.values || dataSource.entities;
  if (!Array.isArray(entities) || entities.length === 0) return [];

  const firstEntity = entities[0];
  const props = firstEntity && firstEntity.properties;
  if (!props) return [];

  if (Array.isArray(props.propertyNames) && props.propertyNames.length > 0) {
    return props.propertyNames.slice(); // copy
  }

  if (typeof props.getValue === "function") {
    const valueObj = props.getValue();
    if (valueObj && typeof valueObj === "object") {
      return Object.keys(valueObj);
    }
  }

  if (props._properties && typeof props._properties === "object") {
    return Object.keys(props._properties);
  }

  if (typeof props === "object") {
    return Object.keys(props);
  }

  return [];
}

// Flatten all Cartesian3 positions to height = 0
function flattenPositionsToZeroHeight(positions, ellipsoid) {
  if (!positions || positions.length === 0) return positions;

  const result = [];
  const ell = ellipsoid || Cesium.Ellipsoid.WGS84;

  for (let i = 0; i < positions.length; i++) {
    const cartesian = positions[i];
    const carto = ell.cartesianToCartographic(cartesian);
    carto.height = 0.0;
    result.push(ell.cartographicToCartesian(carto));
  }

  return result;
}

// Flatten PolygonHierarchy (including holes) to height = 0
function flattenHierarchyToZeroHeight(hierarchy, ellipsoid) {
  if (!hierarchy) return hierarchy;

  const ell = ellipsoid || Cesium.Ellipsoid.WGS84;

  function flatten(h) {
    if (!h) return h;
    const newPositions = flattenPositionsToZeroHeight(h.positions, ell);
    const newHoles = [];

    if (h.holes && h.holes.length > 0) {
      for (let i = 0; i < h.holes.length; i++) {
        newHoles.push(flatten(h.holes[i]));
      }
    }

    return new Cesium.PolygonHierarchy(newPositions, newHoles);
  }

  return flatten(hierarchy);
}

// Main function: flatten entity geometry to height = 0
export function flattenEntityToZeroHeight(entity, ellipsoid) {
  const ell = ellipsoid || Cesium.Ellipsoid.WGS84;
  const now = Cesium.JulianDate.now();

  // Polygon
  if (entity.polygon && entity.polygon.hierarchy) {
    const hierarchy = entity.polygon.hierarchy.getValue(now);
    const flatHierarchy = flattenHierarchyToZeroHeight(hierarchy, ell);
    entity.polygon.hierarchy = new Cesium.ConstantProperty(flatHierarchy);
  }

  // Polyline
  if (entity.polyline && entity.polyline.positions) {
    const positions = entity.polyline.positions.getValue(now);
    const flatPositions = flattenPositionsToZeroHeight(positions, ell);
    entity.polyline.positions = new Cesium.ConstantProperty(flatPositions);
  }

  // You can add more types here (corridor, rectangle, etc.) if needed
}
